import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, MapPin, Star, Send, Download, Instagram } from "lucide-react";
import { toast } from "sonner";

export default function TalentProfile() {
  const { id, slug } = useParams();
  const [t, setT] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const { user } = useAuth();
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [enq, setEnq] = useState({ project: "", role: "", shoot_date: "", location: "", requirements: "", budget: "", usage: "", message: "" });

  useEffect(() => {
    const key = slug || id;
    if (!key) return;
    const path = slug ? `/talents/slug/${slug}` : `/talents/${id}`;
    api.get(path).then((r) => setT(r.data)).catch(() => setT(false));
  }, [id, slug]);

  useEffect(() => {
    if (!t?.full_name) return;
    document.title = `SV Talent Today | ${t.full_name} | ${(t.data?.categories || []).join(" & ") || "Talent"}`;
  }, [t]);

  if (t === false) return <div className="min-h-screen bg-[#0A0A0B]"><Header /><div className="max-w-3xl mx-auto py-32 text-center"><h1 className="font-serif-hero text-4xl text-[#F4EFE4]">Profile not found</h1></div></div>;
  if (!t) return <div className="min-h-screen bg-[#0A0A0B]"><Header /></div>;
  const d = t.data || {};
  const gallery = d.gallery || [];

  const send = async () => {
    try {
      await api.post("/enquiries", { ...enq, talent_id: t.user_id });
      toast.success("Enquiry sent."); setShowEnquiry(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Sign in as casting professional to send enquiries."); }
  };

  const shortlist = async () => {
    try { await api.post("/shortlist", { talent_id: t.user_id }); toast.success("Added to shortlist."); }
    catch { toast.error("Sign in to shortlist."); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <Header />
      <section className="relative">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 pt-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="rounded-2xl overflow-hidden aspect-[3/4] shadow-[0_30px_80px_rgba(14,11,10,0.15)]">
              <img src={d.profile_photo} alt={t.full_name} className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="sv-overline mb-3">{(d.categories || []).join(" · ")}</div>
            <h1 className="font-serif-hero text-5xl lg:text-7xl leading-tight">{t.full_name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#CDC7BA]">
              <div className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#C5A059]" /> Verified Talent</div>
              <div className="flex items-center gap-1.5"><MapPin size={16} /> {d.city}</div>
              <div>{d.availability || "Availability on request"}</div>
            </div>
            <p className="mt-6 max-w-2xl text-[#CDC7BA] leading-relaxed">{d.about}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => setShowEnquiry(true)} data-testid="profile-enquiry" className="sv-btn-primary inline-flex gap-2 items-center"><Send size={14} />Send Enquiry</button>
              <button onClick={shortlist} data-testid="profile-shortlist" className="sv-btn-outline inline-flex gap-2 items-center"><Star size={14} />Shortlist</button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/talents/${t.data?.slug || t.user_id}/casting-book.pdf`}
                target="_blank" rel="noopener noreferrer"
                data-testid="profile-casting-book"
                className="sv-btn-outline inline-flex gap-2 items-center">
                <Download size={14} />Download Casting Book
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-16 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="sv-card p-6">
            <div className="sv-overline mb-3">Physical Profile</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              {["height", "chest", "waist", "hips", "shoe_size", "clothing_size", "hair", "eyes", "skin_tone"].map((k) => d[k] && (
                <div key={k}><div className="font-mono-label text-[10px] text-[#8A8478]">{k.replace("_", " ")}</div><div>{d[k]}</div></div>
              ))}
            </div>
          </div>
          <div className="sv-card p-6">
            <div className="sv-overline mb-3">Skills & Languages</div>
            <div className="flex flex-wrap gap-2">{(d.skills || []).map((s) => <span key={s} className="px-3 py-1 rounded-full bg-[#131316] text-[#C5A059] text-xs">{s}</span>)}</div>
            <div className="mt-3 text-sm text-[#CDC7BA]">{Array.isArray(d.languages) ? d.languages.join(" · ") : d.languages}</div>
          </div>
          {d.gallery?.length > 0 && (
            <div>
              <div className="sv-overline mb-4">Portfolio · {d.gallery.length} {d.gallery.length === 1 ? "photo" : "photos"}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {d.gallery.map((g, i) => (
                  <button key={i} onClick={() => setLightbox(g)} className="group relative overflow-hidden rounded-xl aspect-[3/4]" data-testid={`portfolio-${i}`}>
                    <img src={g} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={`${t.full_name} portfolio ${i + 1}`} loading="lazy" />
                    <div className="absolute bottom-2 right-2 text-[9px] font-mono-label text-[#F4E4BC] bg-[#0A0A0B]/60 px-2 py-1 tracking-[0.28em] opacity-0 group-hover:opacity-100 transition">{String(i + 1).padStart(2, "0")}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {d.videos?.length > 0 && (
            <div>
              <div className="sv-overline mb-4">Videos</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {d.videos.map((v, i) => {
                  const url = typeof v === "string" ? v : v.url;
                  const title = typeof v === "string" ? "Video" : (v.title || "Video");
                  const m = url?.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
                  const embed = m ? `https://www.youtube.com/embed/${m[1]}` : null;
                  return (
                    <div key={i} className="sv-card overflow-hidden">
                      {embed ? (
                        <iframe title={title} src={embed} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full aspect-video" />
                      ) : (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="block p-6 text-[#C5A059] font-mono-label text-[11px]">Open {title} ↗</a>
                      )}
                      {typeof v !== "string" && v.title && <div className="p-3 text-[11px] font-mono-label text-[#CDC7BA]">{v.title}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {d.showreel && (
            <div>
              <div className="sv-overline mb-4">Showreel</div>
              <a href={d.showreel} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] font-mono-label text-[11px]">Watch Showreel ↗</a>
            </div>
          )}
        </div>
        <aside className="space-y-6">
          <div className="sv-card p-6">
            <div className="sv-overline mb-3">Rate Card</div>
            <div className="space-y-2 text-sm">
              {Object.entries(d.rate_card || {}).map(([k, v]) => v && (
                <div key={k} className="flex justify-between border-b border-[#131316] pb-2"><span className="text-[#8A8478]">{k.replace("_", " ")}</span><span>{v}</span></div>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-[#8A8478]">Indicative. Negotiable per project.</div>
          </div>
          <div className="sv-card p-6">
            <div className="sv-overline mb-3">Social</div>
            <div className="text-sm space-y-2">
              {d.instagram && (() => {
                const handle = d.instagram.replace(/^@/, "").trim();
                const url = d.instagram_url || `https://www.instagram.com/${handle}/`;
                return (
                  <a href={url} target="_blank" rel="noopener noreferrer" data-testid="profile-instagram"
                    className="inline-flex items-center gap-2 text-[#C5A059] hover:text-[#F4E4BC] transition">
                    <Instagram size={14} /> @{handle}
                  </a>
                );
              })()}
              {d.youtube && (
                <div><a href={d.youtube.startsWith("http") ? d.youtube : `https://www.youtube.com/${d.youtube.replace(/^@/, "@")}`}
                  target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:text-[#F4E4BC]">YT: {d.youtube}</a></div>
              )}
              {d.imdb && <div><a href={d.imdb} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] hover:text-[#F4E4BC]">IMDb: {d.imdb}</a></div>}
            </div>
          </div>
        </aside>
      </section>

      {showEnquiry && (
        <div className="fixed inset-0 bg-[#F4EFE4]/60 z-50 flex items-center justify-center p-4" onClick={() => setShowEnquiry(false)}>
          <div className="bg-[#1A1A1F] rounded-2xl p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="sv-overline mb-2">Casting Enquiry</div>
            <h3 className="font-serif-hero text-2xl mb-6">Send enquiry to {t.full_name}</h3>
            <div className="grid grid-cols-1 gap-3">
              {["project", "role", "shoot_date", "location", "budget", "usage"].map((f) => (
                <input key={f} className="px-4 py-3 border border-[#5A2E2E] rounded-lg outline-none focus:border-[#C5A059]"
                  placeholder={f.replace("_", " ")} value={enq[f]} onChange={(e) => setEnq({ ...enq, [f]: e.target.value })} data-testid={`enq-${f}`} />
              ))}
              <textarea className="px-4 py-3 border border-[#5A2E2E] rounded-lg min-h-[80px] outline-none focus:border-[#C5A059]"
                placeholder="Message" value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })} />
              <button onClick={send} data-testid="enq-send" className="sv-btn-primary">Send Enquiry</button>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="portfolio-lightbox">
          <img src={lightbox} className="max-h-[92vh] max-w-full object-contain rounded-lg" alt="Full portfolio" />
        </div>
      )}

      <Footer />
    </div>
  );
}
